import {
    AdvanceDeduction,
    AdvanceDeductionMode,
    AdvanceNumbering,
    DocumentType,
    Invoice,
    InvoiceDetails,
    InvoiceLine,
    InvoiceStatus,
    InvoiceTotals,
    PriorRetention,
    RetentionBackfill,
    TaxCategoryCode,
    TaxGroup,
} from '../types';
import { Cents, formatCents, roundCents, taxFromNet } from './money';
import {
    calculateInvoiceTotals,
    groupTaxes,
    invoiceAmountCents,
    ValidationMessage,
} from './invoiceLogic';
import { NEGATIVE_DOCUMENT_TYPES } from './creditNoteLogic';

/**
 * Abschlags-, Schluss- und Restrechnung.
 *
 * Die tragende Regel des ganzen Moduls steht in §14 UStG und in Abschnitt 10
 * der Spezifikation: der Abschlagsabzug mindert AUSSCHLIESSLICH den
 * Zahlbetrag. Der ausgewiesene Nettoumsatz und die ausgewiesene Umsatzsteuer
 * der Schlussrechnung bleiben die der GESAMTEN Leistung — sonst waere der
 * Vorsteuerabzug des Kunden falsch und die Umsatzsteuervoranmeldung zu niedrig.
 *
 * Pur: kein Date.now(), keine Datenbank.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Abschlagsrechnung erzeugen
// ═══════════════════════════════════════════════════════════════════════════

export interface AdvanceRequest {
    /** Bemessungsgrundlage: der Nettowert, auf den sich der Abschlag bezieht. */
    basisNetCents: Cents;
    /** Entweder ein Prozentsatz ODER ein fester Betrag — nie beides. */
    percent?: number;
    fixedNetCents?: Cents;
    taxRateBp: number;
    /**
     * Steuerkategorie nach EN 16931.
     *
     * Muss mitgegeben werden koennen: bei Reverse Charge (§13b UStG) ist der
     * Satz 0 und die Kategorie 'AE'. Waere sie fest 'S', fiele groupTaxes auf
     * die Voreinstellung zurueck und der Pflichthinweis "Steuerschuldnerschaft
     * des Leistungsempfaengers" (§14 Abs. 4 Nr. 8 UStG) erschiene nie auf dem
     * Beleg.
     */
    taxCategoryCode?: TaxCategoryCode;
    title?: string;
    /** Laufende Nummer des Abschlags, erscheint im Positionstext. */
    ordinal?: number;
}

/**
 * Belege, die auf einer kumulativen Rechnung abgezogen werden.
 *
 * Kumulativ heisst: jede Rechnung zeigt den GESAMTEN Leistungsstand und zieht
 * ab, was alle vorherigen Rechnungen dieses Projekts bereits gefordert haben.
 * Der Unterschied zum Abschlagsmodell ist wesentlich — dort wird ein Abschlag
 * beim Abzug VERBRAUCHT (SUPERSEDED_BY_FINAL_INVOICE), hier bleibt er stehen
 * und wird von jeder weiteren Rechnung erneut abgezogen. Wuerde man ihn
 * verbrauchen, zeigte die dritte Rechnung nur noch den Abzug der zweiten, und
 * der Kunde zahlte die erste ein zweites Mal.
 *
 * Deshalb ist SUPERSEDED_BY_FINAL_INVOICE hier abzugsfaehig: der Beleg wurde
 * gestellt und ist bezahlt worden, nur seine Rolle als Abschlag ist beendet.
 * CANCELLED bleibt draussen — diese Forderung besteht nicht mehr.
 */
const DEDUCTIBLE_PRIOR_STATUSES = new Set<InvoiceStatus>([
    'ISSUED',
    'SENT',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'DISPUTED',
    'PARTIALLY_CREDITED',
    'CREDITED',
    'SUPERSEDED_BY_FINAL_INVOICE',
]);

/**
 * Ist dieser Beleg auf einer KUMULATIVEN Rechnung abzugsfaehig?
 *
 * Deckungsgleich mit der Auswahl in deductionsFromPriorInvoices — und
 * ausdruecklich etwas anderes als isDeductibleAdvance: dort muss es eine
 * Abschlagsrechnung sein, hier zaehlt jeder gestellte Beleg des Projekts. Wer
 * beim Ausstellen einer kumulativen Rechnung die Abschlagsregel anlegt, haelt
 * jede gewoehnliche Vorrechnung fuer nicht abzugsfaehig und blockiert einen
 * einwandfreien Beleg.
 */
export const isDeductiblePriorInvoice = (
    inv: Pick<Invoice, 'documentType' | 'status'>,
): boolean =>
    DEDUCTIBLE_PRIOR_STATUSES.has(inv.status) && inv.documentType !== 'CANCELLATION_INVOICE';

/**
 * Menge je Leistung, die die abzugsfaehigen Vorbelege eines Projekts
 * BEANSPRUCHT haben.
 *
 * Das Mengen-Gegenstueck zu deductionsFromPriorInvoices: dieselbe Auswahl an
 * Belegen (isDeductiblePriorInvoice), nur wird nicht der geforderte Betrag
 * gezaehlt, sondern die Menge je serviceId. Gebraucht wird sie, um zu
 * beantworten, fuer welche bereits abgerechnete Menge ueberhaupt ein Beleg
 * existiert (invoiceService: previouslyInvoicedQty, invoiceLogic:
 * uncoveredPriorBilling). In BETRAEGEN laesst sich das nicht beantworten —
 * Nachlass, Preisaenderung und reine Betragskorrektur verschieben den Betrag,
 * ohne die Menge anzufassen.
 *
 * Der EIGENE Anteil je Zeile, nicht die Zeilenmenge. Kumulativ nennt jede Zeile
 * den Gesamtstand; wer ihn ueber die Belege summiert, zaehlt die Menge des
 * ersten Belegs auf jedem weiteren erneut. Derselbe Ausdruck wie in
 * reservedQtyOfLine und im negativen Rueckfall von bookedQuantity:
 *   positiv  max(0, quantity - previouslyBilledQty)  — der Zuwachs
 *   negativ  min(0, quantity + previouslyBilledQty)  — die Ruecknahme
 * Die zweite Zeile ist die Vorzeichenfalle aus mirrorLines: dort wird die Menge
 * negiert, previouslyBilledQty aber unveraendert POSITIV mitkopiert. Roh
 * gerechnet gaebe ein Storno ueber -20 bei 10 Vorabgerechneten -30 zurueck und
 * riss eine Deckungsluecke auf, die es nie gab.
 *
 * Vorzeichen zusaetzlich an der Belegart festgemacht (NEGATIVE_DOCUMENT_TYPES),
 * genau wie beim Betrag: eine kaufmaennische Gutschrift MINDERT die gedeckte
 * Menge, auch wenn ihre Zeilen positiv erfasst wurden.
 *
 * Ein STORNIERTER Beleg deckt nichts mehr — er steht auf CANCELLED und faellt
 * schon aus isDeductiblePriorInvoice; sein Stornobeleg ebenfalls. Das Paar
 * verschwindet damit vollstaendig, statt sich zu -Menge zu addieren.
 *
 * Zeilen mit quantityInformational tragen nichts bei: dort benennen Menge und
 * Preis nur die Ursprungsposition einer reinen Betragskorrektur.
 */
export const invoicedQuantityByService = (
    invoices: Pick<Invoice, 'id' | 'documentType' | 'status' | 'lines'>[],
    excludeInvoiceId?: string,
): Map<string, number> => {
    const out = new Map<string, number>();
    for (const inv of invoices) {
        if (inv.id === excludeInvoiceId) continue;
        if (!isDeductiblePriorInvoice(inv)) continue;
        const negative = NEGATIVE_DOCUMENT_TYPES.has(inv.documentType);
        for (const l of inv.lines ?? []) {
            if (l.kind !== 'SERVICE' || !l.serviceId) continue;
            if (l.quantityInformational) continue;
            const qty = l.quantity ?? 0;
            const prev = l.previouslyBilledQty ?? 0;
            const claimed = qty >= 0 ? Math.max(0, qty - prev) : Math.min(0, qty + prev);
            if (claimed === 0) continue;
            const signed = negative ? -Math.abs(claimed) : claimed;
            out.set(l.serviceId, (out.get(l.serviceId) ?? 0) + signed);
        }
    }
    return out;
};

/**
 * Was ein Beleg in EINER Steuergruppe SELBST gefordert hat — nicht, was er
 * ausgewiesen hat.
 *
 * Kumulativ nennt jeder Beleg den gesamten Leistungsstand und zieht die
 * frueheren ab. Beleg 1 forderte 831,50; Beleg 2 wies 1.163,00 aus und zog
 * davon 831,50 ab, forderte also 331,50. Wer beide ausgewiesenen Betraege
 * addiert, zaehlt die 831,50 ein zweites Mal — der Abzug uebersteigt dann die
 * Gesamtleistung, und der naechste Beleg laesst sich gar nicht mehr ausstellen.
 *
 * Der eigene Abzug des Belegs steht bereits je Steuersatz fest; abgezogen wird
 * also exakt, ohne den Betrag auf die Saetze verteilen zu muessen.
 *
 * Eine Funktion fuer zwei Leser: deductionsFromPriorInvoices bildet daraus den
 * Abzug, priorRetentionsFromDeductions erkennt daran die Vorbelege OHNE
 * Forderung, die der Abzug uebersprungen hat. Zwei Formulierungen derselben
 * Regel liefen irgendwann auseinander.
 */
const demandedByGroup = (
    inv: Pick<Invoice, 'advanceDeductions'>,
    g: Pick<TaxGroup, 'taxRateBp' | 'taxableNetCents' | 'taxCents'>,
): { demandedNet: Cents; demandedTax: Cents } => {
    const ownDeduction = (inv.advanceDeductions ?? [])
        .filter((d) => d.taxRateBp === g.taxRateBp)
        .reduce(
            (acc, d) => ({ net: acc.net + d.netCents, tax: acc.tax + d.taxCents }),
            { net: 0, tax: 0 },
        );
    return {
        demandedNet: g.taxableNetCents - ownDeduction.net,
        demandedTax: g.taxCents - ownDeduction.tax,
    };
};

/**
 * Fordert dieser Beleg in KEINER Steuergruppe etwas? Genau diese Belege laesst
 * deductionsFromPriorInvoices aus — sie haetten einen Abzug ueber 0.
 */
const demandsNothing = (inv: Pick<Invoice, 'advanceDeductions' | 'totals'>): boolean =>
    inv.totals.taxGroups.every((g) => {
        const { demandedNet, demandedTax } = demandedByGroup(inv, g);
        return demandedNet === 0 && demandedTax === 0;
    });

/**
 * Abzugsliste aus allen vorher ausgestellten Belegen eines Projekts.
 *
 * Je Steuersatz ein Eintrag, damit der Abzug §14 UStG genuegt: die enthaltene
 * Umsatzsteuer muss zuordenbar bleiben.
 */
export const deductionsFromPriorInvoices = (
    invoices: Invoice[],
    excludeInvoiceId?: string,
): AdvanceDeduction[] => {
    const out: AdvanceDeduction[] = [];
    for (const inv of invoices) {
        if (inv.id === excludeInvoiceId) continue;
        if (!DEDUCTIBLE_PRIOR_STATUSES.has(inv.status)) continue;
        // Stornobelege bleiben draussen, weil ihr Ursprungsbeleg es schon ist:
        // er steht auf CANCELLED und faellt oben heraus. Zaehlte das Storno
        // trotzdem mit, ergaebe das Paar nicht null, sondern den negativen
        // Ursprungsbetrag — die naechste Rechnung forderte den stornierten
        // Betrag ein zweites Mal vom Kunden.
        if (inv.documentType === 'CANCELLATION_INVOICE') continue;

        // Vorzeichen je Belegart: eine Gutschrift mindert die bisherige
        // Forderung, also auch den Abzug. Bei allen uebrigen Arten bleibt das
        // gespeicherte Vorzeichen stehen — eine Korrekturrechnung nach unten
        // traegt bereits negative Summen, und den Betrag heraufzuholen kehrte
        // die Korrektur um.
        const negative = NEGATIVE_DOCUMENT_TYPES.has(inv.documentType);
        for (const g of inv.totals.taxGroups) {
            const { demandedNet, demandedTax } = demandedByGroup(inv, g);
            if (demandedNet === 0 && demandedTax === 0) continue;
            const net = negative ? -Math.abs(demandedNet) : demandedNet;
            const tax = negative ? -Math.abs(demandedTax) : demandedTax;
            out.push({
                advanceInvoiceId: inv.id,
                invoiceNumber: inv.invoiceNumber ?? '',
                invoiceDate: inv.invoiceDate,
                taxRateBp: g.taxRateBp,
                netCents: net,
                taxCents: tax,
                grossCents: net + tax,
            });
        }
    }
    return out.sort(
        (a, b) =>
            a.invoiceDate.localeCompare(b.invoiceDate) ||
            a.invoiceNumber.localeCompare(b.invoiceNumber) ||
            b.taxRateBp - a.taxRateBp,
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Sicherheitseinbehalt: Einbehalte der abgezogenen Vorbelege
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Gibt dieser Beleg die Einbehalte frei? Nur die Schlussrechnung, und nur,
 * wenn der Bearbeiter das nicht abgeschaltet hat (details.releaseRetention
 * === false). Bei INVOICE und ADVANCE_INVOICE gilt der Satz des Belegs.
 *
 * Die Freigabe selbst ist keine eigene Liste mehr, sondern die Folge des
 * Satzes 0 in der kumulativen Formel (effectiveRetentionPercent,
 * computeRetention): cumulative 0 minus prior = alles frei.
 */
export const wantsRetentionRelease = (
    documentType: DocumentType,
    details: InvoiceDetails | null | undefined,
): boolean => documentType === 'FINAL_INVOICE' && details?.releaseRetention !== false;

/**
 * Netto auf einem Beleg einbehalten: eigener Einbehalt minus eigene Freigabe,
 * vorzeichenrichtig nach Belegart.
 *
 * Bei NEGATIVE_DOCUMENT_TYPES negativ wie beim Abzug (deductionsFromPriorInvoices):
 * eine kaufmaennische Gutschrift mindert das bisher Einbehaltene. Bei allen
 * uebrigen Arten zaehlt das gespeicherte Vorzeichen — eine Korrektur nach
 * unten traegt ihren Einbehalt bereits negativ (Spiegelung in creditNoteLogic),
 * und eine Schlussrechnung, die freigegeben hat, ist netto negativ: genau das
 * macht prior nach der Freigabe zu 0.
 */
const netRetainedCents = (inv: Pick<Invoice, 'documentType' | 'totals'>): Cents => {
    const net = (inv.totals.retentionCents ?? 0) - (inv.totals.retentionReleaseCents ?? 0);
    return NEGATIVE_DOCUMENT_TYPES.has(inv.documentType) ? -Math.abs(net) : net;
};

/**
 * Die Einbehalte der Vorbelege, die dieser Beleg abzieht — je DISTINCT
 * advanceInvoiceId der Abzuege ein Eintrag, in der Reihenfolge des ersten
 * Auftretens (die Abzugsliste ist bereits nach Datum und Nummer sortiert).
 *
 * Aus den ABZUEGEN abgeleitet und nicht aus allen Vorbelegen des Projekts:
 * prior muss genau zu prepaid passen, sonst stimmt paidPrior = prepaid - prior
 * nicht, und ein fortschreibender Beleg ohne Abzuege hat zu Recht prior 0.
 * Ein Beleg ueber mehrere Steuersaetze steht mehrfach in den Abzuegen, aber
 * nur einmal hier — der Einbehalt hat keinen Steueranteil.
 *
 * Je Quelle: der gespeicherte Nettoeinbehalt des Quellbelegs (netRetainedCents)
 * plus alle Backfill-Eintraege mit sourceRef === advanceInvoiceId. Der Backfill
 * ist der von Hand nachgetragene Alt-Einbehalt fuer Belege, die selbst keinen
 * tragen (Altbestand vor dem Feature, Satzwechsel); ohne ihn holte die Formel
 * den vollen Satz auf dem naechsten Beleg nach, obwohl der Auftraggeber laengst
 * einbehalten hat. Externe Alt-Abschlaege (`ext:`) haben keinen Beleg im
 * System und damit nur Backfill. Nummer und Datum kommen vom Abzug — fuer
 * `ext:` gibt es sonst keine Quelle.
 *
 * Eintraege mit Summe 0 entfallen: sie stuenden ohne Betrag in der
 * Einzelaufstellung.
 *
 * ZUSATZREGEL — Vorbelege ohne Forderung. deductionsFromPriorInvoices
 * ueberspringt Belege, die selbst nichts gefordert haben (jede Steuergruppe
 * auf 0: demandsNothing). Eine Schlussrechnung OHNE neue Leistung ist genau
 * so ein Beleg: Leistungsstand gleich Vorrechnungen, Forderung 0, aber
 * Freigabe -258.632. Sie stuende nie in den Abzuegen eines spaeteren Belegs,
 * ihre Freigabe fehlte in prior, und eine zweite Schlussrechnung gaebe alles
 * ein zweites Mal frei. Deshalb kommen zusaetzlich alle Belege aus `invoices`
 * herein, die abzugsfaehig sind (isDeductiblePriorInvoice), NICHT in den
 * Abzuegen stehen, nichts fordern und netto einen Einbehalt ungleich 0 tragen
 * — Nummer und Datum vom Beleg, denn einen Abzug gibt es nicht.
 *
 * Zwei Schranken, beide tragend:
 *   - Nur "fordert nichts" und nicht "fehlt in den Abzuegen": ein Beleg mit
 *     Forderung, der trotzdem fehlt, gehoert einer anderen Partei
 *     (Teilrechnung, splitScope) oder wurde nach dem Bilden der Abzuege
 *     ausgestellt. Beides darf prior nicht veraendern — prior muss zu prepaid
 *     passen, und prepaid kennt ihn auch nicht.
 *   - Nur bei einem Beleg MIT Abzuegen: ein fortschreibender Beleg ohne
 *     Abzuege hat prior 0, weil gross dort nur die eigene neue Leistung ist.
 *     Zoege er die Freigabe einer frueheren Schlussrechnung herein, hielte er
 *     sie auf seiner eigenen Leistung ein zweites Mal ein.
 *
 * Der eigene Beleg (excludeInvoiceId) bleibt draussen. Heute ist er als
 * Entwurf ohnehin nicht abzugsfaehig; der Parameter schuetzt den Tag, an dem
 * ein Aufrufer die Liste fuer einen bereits ausgestellten Beleg neu bildet.
 */
export const priorRetentionsFromDeductions = (
    deductions: AdvanceDeduction[],
    invoices: Invoice[],
    backfill: RetentionBackfill[],
    excludeInvoiceId?: string,
): PriorRetention[] => {
    const byId = new Map<string, Invoice>();
    for (const inv of invoices) byId.set(inv.id, inv);

    const out: PriorRetention[] = [];
    const seen = new Set<string>();
    for (const d of deductions) {
        if (seen.has(d.advanceInvoiceId)) continue;
        seen.add(d.advanceInvoiceId);
        if (d.advanceInvoiceId === excludeInvoiceId) continue;

        const source = byId.get(d.advanceInvoiceId);
        const stored = source ? netRetainedCents(source) : 0;
        const filled = backfill.filter((b) => b.sourceRef === d.advanceInvoiceId);
        const backfilled = filled.reduce((sum, b) => sum + b.retentionCents, 0);
        const retentionCents = stored + backfilled;
        if (retentionCents === 0) continue;

        out.push({
            sourceId: d.advanceInvoiceId,
            invoiceNumber: d.invoiceNumber,
            invoiceDate: d.invoiceDate,
            retentionCents,
            ...(filled.length > 0 ? { backfilled: true } : {}),
        });
    }

    if (deductions.length === 0) return out;
    for (const inv of invoices) {
        if (seen.has(inv.id) || inv.id === excludeInvoiceId) continue;
        if (!isDeductiblePriorInvoice(inv) || !demandsNothing(inv)) continue;
        seen.add(inv.id);
        // Kein Backfill fuer diese Belege: der Backfill haengt an der Kennung
        // eines ABZUGS, und einen Abzug hat ein forderungsloser Beleg nicht.
        const retentionCents = netRetainedCents(inv);
        if (retentionCents === 0) continue;
        out.push({
            sourceId: inv.id,
            invoiceNumber: inv.invoiceNumber ?? '',
            invoiceDate: inv.invoiceDate,
            retentionCents,
        });
    }
    return out;
};

/**
 * Summe der Liste — deckungsgleich mit InvoiceTotals.priorRetentionCents, das
 * calculateInvoiceTotals aus derselben Liste bildet. Fuer die Summenzeile unter
 * der Einzelaufstellung.
 */
export const priorRetentionTotal = (list: PriorRetention[]): Cents =>
    list.reduce((s, r) => s + r.retentionCents, 0);

/**
 * Was auf dem Projekt insgesamt noch einbehalten ist — fuer die Projektpruefung
 * ("Offene Sicherheitseinbehalte: 395,00 €", Warnung bei einer
 * Schlussrechnung, die nicht freigibt).
 *
 * Summe des Nettoeinbehalts (netRetainedCents) ueber alle abzugsfaehigen
 * Vorbelege (isDeductiblePriorInvoice, ohne den eigenen) plus alle
 * Backfill-Eintraege des Projekts. Nach einer Schlussrechnung, die freigegeben
 * hat, ergibt das 0: sie ist selbst abzugsfaehig und netto um genau die Summe
 * der Vorbelege negativ. Wird sie storniert, steht sie auf CANCELLED und faellt
 * heraus, ihr Stornobeleg wird als CANCELLATION_INVOICE uebersprungen — die
 * Einbehalte sind wieder offen. Eine Sperre gegen doppelte Freigabe braucht es
 * deshalb nicht mehr.
 *
 * Hier ueber ALLE Vorbelege und nicht ueber die Abzuege eines Belegs, weil die
 * Frage eine andere ist: nicht "was zieht dieser Beleg ab", sondern "was liegt
 * beim Auftraggeber". Der Backfill zaehlt ungekuerzt mit — er beschreibt
 * Einbehalte, die die Belege selbst nicht kennen.
 */
export const openRetentionCents = (
    invoices: Invoice[],
    backfill: RetentionBackfill[],
    excludeInvoiceId?: string,
): Cents => {
    let sum = 0;
    for (const inv of invoices) {
        if (inv.id === excludeInvoiceId) continue;
        if (!isDeductiblePriorInvoice(inv)) continue;
        sum += netRetainedCents(inv);
    }
    for (const b of backfill) sum += b.retentionCents;
    return sum;
};

/**
 * Erzeugt die Position einer Abschlagsrechnung.
 *
 * Bewusst EINE Position und keine Kopie des Leistungsverzeichnisses: ein
 * Abschlag ist eine Zahlungsanforderung auf den Gesamtauftrag, keine
 * Abrechnung einzelner Leistungen. Wuerde man die LV-Positionen kopieren,
 * muesste man sie beim Ausstellen ins Projekt zurueckschreiben — und genau das
 * darf ein Abschlag nicht, denn die Leistung ist noch nicht erbracht.
 */
/**
 * Der Nettobetrag eines Abschlags — EINMAL gerechnet, an einer Stelle.
 *
 * Eigene Funktion, weil ihn zwei Wege brauchen: die Zeile des Belegs und die
 * Aufteilung auf mehrere Auftraggeber (createSplitAdvanceInvoices). Die
 * Aufteilung MUSS den fertigen Betrag teilen und nicht die Bemessungsgrundlage
 * — sonst wird zweimal gerundet: 2 Cent Grundlage, 50 %, haelftig geteilt
 * ergaeben zweimal 1 Cent, waehrend der ungeteilte Abschlag 1 Cent betraegt.
 * Ein zweiter Rechenweg an zweiter Stelle waere genau die Groesse, die
 * auseinanderlaeuft.
 */
export const advanceNetCents = (req: AdvanceRequest): Cents => {
    if (req.percent != null && req.fixedNetCents != null) {
        throw new Error('Ein Abschlag hat entweder einen Prozentsatz oder einen Festbetrag.');
    }
    return req.fixedNetCents != null
        ? roundCents(req.fixedNetCents)
        : roundCents((req.basisNetCents * (req.percent ?? 0)) / 100);
};

export const buildAdvanceLine = (req: AdvanceRequest, lineId: string): InvoiceLine => {
    const netCents = advanceNetCents(req);

    if (netCents <= 0) {
        throw new Error('Der Abschlagsbetrag muss größer als 0,00 € sein.');
    }

    const label =
        req.title ??
        (req.percent != null
            ? `${req.ordinal ? `${req.ordinal}. ` : ''}Abschlag (${req.percent} % von ${formatCents(req.basisNetCents)})`
            : `${req.ordinal ? `${req.ordinal}. ` : ''}Abschlag`);

    return {
        id: lineId,
        kind: 'SERVICE',
        posNumber: '1',
        title: label,
        unit: 'pauschal',
        quantity: 1,
        unitPriceCents: netCents,
        netCents,
        taxRateBp: req.taxRateBp,
        // taxCategoryCode, nicht taxCategory: der falsche Feldname rutschte
        // durch die Typzusicherung, blieb zur Laufzeit undefined, und
        // groupTaxes fiel auf die Voreinstellung zurueck.
        taxCategoryCode: req.taxCategoryCode ?? (req.taxRateBp > 0 ? 'S' : 'Z'),
    } as InvoiceLine;
};

// ═══════════════════════════════════════════════════════════════════════════
// Abschlaege in die Schlussrechnung uebernehmen
// ═══════════════════════════════════════════════════════════════════════════

/** Belegarten, die als Abschlag in eine Schlussrechnung eingehen. */
const ADVANCE_TYPES = new Set<DocumentType>(['ADVANCE_INVOICE']);

/**
 * Status, in denen ein Abschlag noch abzugsfaehig ist.
 *
 * Ein stornierter Abschlag darf NICHT abgezogen werden — er ist rueckabgewickelt
 * und wurde nie geschuldet. Ein bereits durch eine andere Schlussrechnung
 * abgeloester ebenso wenig, sonst zoege man ihn zweimal ab.
 */
export const DEDUCTIBLE_ADVANCE_STATUSES: InvoiceStatus[] = [
    'ISSUED',
    'SENT',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'DISPUTED',
];

const DEDUCTIBLE_STATUSES = new Set<InvoiceStatus>(DEDUCTIBLE_ADVANCE_STATUSES);

export const isDeductibleAdvance = (inv: Pick<Invoice, 'documentType' | 'status'>): boolean =>
    ADVANCE_TYPES.has(inv.documentType) && DEDUCTIBLE_STATUSES.has(inv.status);

/**
 * Baut die Abzugsliste aus den vorangegangenen Abschlagsrechnungen.
 *
 * Je Steuersatz eines Abschlags entsteht ein eigener Eintrag — ein Abschlag
 * ueber mehrere Saetze wird also aufgeteilt. Nur so kann die Schlussrechnung
 * spaeter je Satz sauber abziehen, was §14 UStG verlangt.
 */
export const deductionsFromAdvances = (advances: Invoice[]): AdvanceDeduction[] => {
    const out: AdvanceDeduction[] = [];
    for (const adv of advances) {
        if (!isDeductibleAdvance(adv)) continue;
        for (const g of adv.totals.taxGroups) {
            if (g.taxableNetCents === 0 && g.taxCents === 0) continue;
            out.push({
                advanceInvoiceId: adv.id,
                invoiceNumber: adv.invoiceNumber ?? '',
                invoiceDate: adv.invoiceDate,
                taxRateBp: g.taxRateBp,
                netCents: g.taxableNetCents,
                taxCents: g.taxCents,
                grossCents: g.taxableNetCents + g.taxCents,
            });
        }
    }
    // Nach Rechnungsdatum, dann nach Nummer — die Einzelaufstellung auf dem
    // Beleg soll der zeitlichen Reihenfolge folgen, in der die Abschlaege
    // gestellt wurden.
    return out.sort(
        (a, b) =>
            a.invoiceDate.localeCompare(b.invoiceDate) ||
            a.invoiceNumber.localeCompare(b.invoiceNumber),
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Pruefungen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Haeufigste Ursache beider Blocker in Bestandsprojekten: die Alt-Abschlaege
 * sind nachgetragen, die Leistung dahinter aber nie im Projekt erfasst worden.
 * Der Hinweis nennt den Weg, statt den Nutzer die Vertragsmengen hochdrehen zu
 * lassen — das waere derselbe Betrag ohne Herkunft und ohne Datum.
 */
const BACKFILL_HINT =
    ' Fehlen im Projekt Alt-Positionen aus der Zeit vor der App? Über „Letzte Rechnung einlesen“ in den Vorrechnungen können sie ergänzt werden.';

/** Ein Steuersatz, dessen Vorbelege mehr fordern, als im LV abgerechnet steht. */
export interface PriorBilledGap {
    taxRateBp: number;
    /** Summe der abgezogenen Vorbelege (netto). */
    deductedCents: Cents;
    /** Abgerechneter LV-Stand (previouslyBilledQty × Einzelpreis) plus Zuschlag dieses Belegs. */
    billedBasisCents: Cents;
    /** deductedCents − billedBasisCents, immer > Toleranz. */
    gapCents: Cents;
}

/** Rundung ueber viele Altbelege — darunter ist eine Luecke keine Aussage. */
const PRIOR_BILLED_TOLERANCE_CENTS = 100;

/**
 * Fordern die Vorbelege einer KUMULATIVEN Rechnung mehr, als das LV als
 * abgerechnet fuehrt?
 *
 * Kumulativ wird der Gesamtstand gefordert und JEDER Vorbeleg abgezogen. Der
 * abgerechnete Stand (quantityBilled, hier previouslyBilledQty) geht in den
 * Betrag gar nicht ein — deshalb faellt es nicht auf, wenn er zu niedrig steht.
 * Genau dann steht aber auch der Leistungsstand zu niedrig: die Alt-Rechnungen
 * fordern Leistung, die im Projekt fehlt, und die Rechnung faellt um diesen
 * Betrag zu niedrig aus. Der Blocker advance_exceeds_rate_basis greift erst,
 * wenn die Luecke groesser ist als alles Offene — darunter geht das Geld
 * lautlos verloren (5940: 2.975 EUR). In 5284 (29.09.2026) war sie mit
 * 9.955 EUR (11 Begehungen, SiGe-Plan, Vorankuendigung) groesser als die 7
 * offenen Begehungen, dort griff der Blocker — die Ursache nannte er nicht.
 *
 * Verglichen wird je Steuersatz: Vorbelege netto gegen die Summe
 * previouslyBilledQty × Einzelpreis der Leistungszeilen plus den Zuschlag
 * (Nebenkosten) dieses Belegs als Obergrenze — die Vorbelege tragen ihren
 * eigenen, und mehr als heute kann es nicht gewesen sein. Das kostet
 * Empfindlichkeit (eine Luecke unter dem Zuschlag auf die offene Menge bleibt
 * stumm) und schliesst Fehlalarme aus.
 * Ein Nachlass zaehlt bewusst NICHT: er senkte die Grundlage und meldete jeden
 * Beleg mit Nachlass faelschlich; ein Nachlass auf den Vorbelegen senkt deren
 * Summe und bleibt ohnehin unter der Schwelle.
 *
 * Nicht ermittelbar (eine Leistungszeile ohne previouslyBilledQty — Altentwurf
 * oder freie Zeile) heisst: keine Aussage fuer diesen Satz, statt zu raten.
 * Der kumulative Zeilenbau setzt den Wert an jeder Zeile, auch als 0.
 */
export const priorExceedsBilledStand = (
    lines: InvoiceLine[],
    deductions: AdvanceDeduction[],
): PriorBilledGap[] => {
    const out: PriorBilledGap[] = [];
    const rates = new Set(deductions.map((d) => d.taxRateBp));
    for (const rate of rates) {
        const service = lines.filter((l) => l.kind === 'SERVICE' && (l.taxRateBp ?? 0) === rate);
        if (!service.length) continue;
        if (service.some((l) => l.previouslyBilledQty === undefined)) continue;
        const serviceNet = service.reduce((s, l) => s + (l.netCents || 0), 0);
        if (serviceNet <= 0) continue;
        const surcharge = lines
            .filter((l) => l.kind === 'SURCHARGE' && (l.taxRateBp ?? 0) === rate)
            .reduce((s, l) => s + Math.max(0, l.netCents || 0), 0);
        const billedNet = service.reduce(
            (s, l) => s + roundCents((l.previouslyBilledQty ?? 0) * (l.unitPriceCents || 0)),
            0,
        );
        // Der GANZE Zuschlag dieses Belegs als Obergrenze dessen, was die
        // Vorbelege an Zuschlag enthalten koennen: bei Prozent-Nebenkosten ist
        // deren Anteil hoechstens billed/Leistung davon, bei einer Pauschale
        // hoechstens die Pauschale selbst. Hochrechnen im Verhaeltnis meldete
        // eine Pauschale faelschlich (10.000 + 500 vorher, 20.000 + 500 jetzt
        // → Grundlage 10.250 statt 10.500).
        const billedBasisCents = billedNet + surcharge;
        const deductedCents = deductions
            .filter((d) => d.taxRateBp === rate)
            .reduce((s, d) => s + d.netCents, 0);
        const gapCents = deductedCents - billedBasisCents;
        if (gapCents > PRIOR_BILLED_TOLERANCE_CENTS) {
            out.push({ taxRateBp: rate, deductedCents, billedBasisCents, gapCents });
        }
    }
    return out;
};

/**
 * Prueft die Abschlagskette einer Schlussrechnung.
 *
 * Der wichtigste Fall ist der letzte: uebersteigen die Abschlaege die
 * Gesamtleistung, ist der Zahlbetrag negativ. Das System darf nie einen
 * negativen Zahlbetrag ausweisen — der Ueberhang ist ein Kundenguthaben oder
 * ein Erstattungsfall, keine Forderung.
 */
export const validateAdvanceChain = (
    lines: InvoiceLine[],
    deductions: AdvanceDeduction[],
    documentType: DocumentType,
    /**
     * Kumulativ werden ALLE Vorbelege des Projekts abgezogen, auch Gutschriften
     * — und deren Abzug ist zu Recht negativ. Im Abschlagsmodell dagegen sind
     * ausschliesslich Abschlagsrechnungen im Spiel, und ein negativer Abschlag
     * ist immer ein Fehler.
     */
    opts: { cumulative?: boolean } = {},
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    const err = (code: string, message: string) => out.push({ code, level: 'error', message });
    const warn = (code: string, message: string) => out.push({ code, level: 'warning', message });
    // Kumulativ ist jeder Vorbeleg gemeint, nicht nur Abschlagsrechnungen. Wer
    // "Abschlagsrechnung" liest, sucht nach einem Abschlag, den es gar nicht
    // geben muss.
    const doc = opts.cumulative ? 'Der Beleg' : 'Die Abschlagsrechnung';

    if (deductions.length === 0) {
        if (documentType === 'FINAL_INVOICE' || documentType === 'REMAINING_INVOICE') {
            warn(
                'final_without_advances',
                'Es werden keine Abschläge abgezogen. Falls Abschlagsrechnungen existieren, fehlt hier ihr Abzug.',
            );
        }
        return out;
    }

    const seen = new Set<string>();
    for (const d of deductions) {
        const key = `${d.advanceInvoiceId}_${d.taxRateBp}`;
        if (seen.has(key)) {
            err(
                'advance_deducted_twice',
                `${doc} ${d.invoiceNumber} wird für denselben Steuersatz mehrfach abgezogen.`,
            );
        }
        seen.add(key);

        if (d.netCents + d.taxCents !== d.grossCents) {
            err(
                'advance_amounts_inconsistent',
                `Beim Abzug der Abschlagsrechnung ${d.invoiceNumber} ergeben Netto und Steuer nicht den Bruttobetrag.`,
            );
        }
        if (!d.invoiceNumber) {
            err(
                'advance_without_number',
                'Ein abzuziehender Abschlag hat keine Rechnungsnummer — dann war er nie ausgestellt.',
            );
        }

        // Ein negativer Abzug ERHOEHT den Zahlbetrag. Keine der uebrigen
        // Pruefungen faengt ihn: die Satzsumme wird durch ihn kleiner statt
        // groesser, und der Zahlbetrag steigt statt zu sinken. Der Kunde
        // muesste mehr zahlen, ohne dass etwas anschlaegt.
        //
        // Kumulativ ist ein einzelner negativer Eintrag dagegen der Normalfall:
        // eine Gutschrift mindert das bisher Geforderte, und genau deshalb muss
        // die naechste Rechnung entsprechend mehr fordern. Dort zaehlt die
        // Summe je Steuersatz, nicht der einzelne Beleg — siehe unten.
        if (!opts.cumulative && (d.netCents < 0 || d.taxCents < 0 || d.grossCents < 0)) {
            err(
                'advance_negative',
                `Der Abzug der Abschlagsrechnung ${d.invoiceNumber || '(ohne Nummer)'} ist negativ. Ein negativer Abzug erhöht den Zahlbetrag.`,
            );
        }
    }

    // Je Steuersatz darf nicht mehr abgezogen werden, als in diesem Satz
    // ueberhaupt geleistet wurde. Sonst entstuende eine negative
    // Bemessungsgrundlage — ein Beleg, den kein Finanzamt akzeptiert.
    const taxGroups = groupTaxes(lines);
    for (const g of taxGroups) {
        const deducted = deductions
            .filter((d) => d.taxRateBp === g.taxRateBp)
            .reduce((s, d) => s + d.netCents, 0);
        if (deducted > g.taxableNetCents) {
            err(
                'advance_exceeds_rate_basis',
                `Zum Steuersatz ${g.taxRateBp / 100} % werden ${formatCents(deducted)} abgezogen, erbracht wurden aber nur ${formatCents(g.taxableNetCents)}.${BACKFILL_HINT}`,
            );
        }
        // Kumulativ ersetzt die Satzsumme die Einzelpruefung auf ein negatives
        // Vorzeichen. Einzelne Gutschriften duerfen negativ sein; ist die SUMME
        // negativ, wurde mehr gutgeschrieben als je gefordert — dann erhoehte
        // der "Abzug" den Zahlbetrag ueber die Leistung hinaus, und der Kunde
        // zahlte fuer eine Gutschrift, die er bereits erhalten hat.
        if (opts.cumulative && deducted < 0) {
            err(
                'prior_deduction_negative',
                `Zum Steuersatz ${g.taxRateBp / 100} % ergeben die bisherigen Belege einen negativen Abzug von ${formatCents(-deducted)}. Es wurde mehr gutgeschrieben als jemals berechnet — das erhöht den Zahlbetrag statt ihn zu mindern.`,
            );
        }
    }

    // Steuersaetze, die es in den Abschlaegen gibt, aber nicht in der Leistung.
    for (const d of deductions) {
        if (!taxGroups.some((g) => g.taxRateBp === d.taxRateBp)) {
            err(
                'advance_rate_not_in_invoice',
                `${doc} ${d.invoiceNumber} enthält den Steuersatz ${d.taxRateBp / 100} %, der in dieser Rechnung nicht vorkommt.`,
            );
        }
    }

    if (opts.cumulative) {
        for (const w of priorExceedsBilledStand(lines, deductions)) {
            warn(
                'prior_exceeds_billed_stand',
                `Zum Steuersatz ${w.taxRateBp / 100} % fordern die bisherigen Belege ${formatCents(w.deductedCents)} netto, als abgerechnet erfasst sind im Leistungsverzeichnis aber nur ${formatCents(w.billedBasisCents)}. ${formatCents(w.gapCents)} stehen keinem abgerechneten Leistungsstand gegenüber — um bis zu diesen Betrag fordert diese Rechnung zu wenig. Meist fehlt Altbestand im Projekt: abgerechnete Mengen gegen die Alt-Rechnungen prüfen.`,
            );
        }
    }

    const totals = calculateInvoiceTotals(lines, deductions);
    if (totals.payableCents < 0) {
        err(
            'payable_negative',
            `${opts.cumulative ? 'Die bisherigen Belege übersteigen' : 'Die Abschläge übersteigen'} die Gesamtleistung um ${formatCents(-totals.payableCents)}. Der Überhang ist ein Kundenguthaben und wird nicht als Forderung ausgewiesen.${BACKFILL_HINT}`,
        );
    }

    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Restbetrag
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der noch offene Betrag einer Leistung nach allen Abschlaegen.
 *
 * Gerechnet wird BRUTTO, weil der Kunde brutto zahlt. Die Aufteilung in Netto
 * und Steuer bleibt davon unberuehrt — der Abzug mindert nur den Zahlbetrag.
 */
export const remainingGrossCents = (lines: InvoiceLine[], deductions: AdvanceDeduction[]): Cents =>
    calculateInvoiceTotals(lines, deductions).payableCents;

/**
 * Fasst die Abschlagskette fuer die Anzeige zusammen.
 *
 * Enthaelt bewusst beide Sichten: die Summe je Steuersatz (fuer den Beleg) und
 * die Summe insgesamt (fuer die Uebersicht). Die Summe insgesamt wird NIE als
 * einzelne Abzugszeile gedruckt, wenn mehrere Saetze vorkommen.
 */
export const summarizeAdvances = (
    deductions: AdvanceDeduction[],
): {
    count: number;
    invoiceNumbers: string[];
    netCents: Cents;
    taxCents: Cents;
    grossCents: Cents;
} => {
    const invoiceNumbers = [...new Set(deductions.map((d) => d.invoiceNumber).filter(Boolean))];
    return {
        count: invoiceNumbers.length,
        invoiceNumbers,
        netCents: deductions.reduce((s, d) => s + d.netCents, 0),
        taxCents: deductions.reduce((s, d) => s + d.taxCents, 0),
        grossCents: deductions.reduce((s, d) => s + d.grossCents, 0),
    };
};

/**
 * Bruttosumme aller Abzugszeilen.
 *
 * Rein nachrichtlich: die Einzelaufstellung (Modus INDIVIDUAL) nennt jeden
 * Abschlag mit seiner Rechnungsnummer, und darunter soll stehen, was zusammen
 * abgezogen wird. Bewusst NUR brutto und ohne Steuerangabe — eine gesammelte
 * Steuerzeile waere bei mehreren Saetzen nicht §14-UStG-konform; die Zuordnung
 * der enthaltenen Umsatzsteuer leisten die Einzelzeilen und der Steuerblock.
 *
 * Deckungsgleich mit InvoiceTotals.prepaidCents — calculateInvoiceTotals
 * summiert dieselben grossCents. Die Zeile ist damit reine Darstellung und kann
 * den Zahlbetrag nicht verschieben.
 *
 * Ob sie ueberhaupt erscheint, entscheidet advanceDeductionRangeLabel: bei nur
 * EINEM Abschlag waere sie die Wiederholung der Zeile darueber. Diese
 * Anzeigeregel gehoert an die Beschriftung und nicht hierher — eine Summe ueber
 * einen Posten ist rechnerisch richtig, nur unsinnig zu drucken.
 */
export const advanceDeductionTotal = (deductions: AdvanceDeduction[]): Cents =>
    deductions.reduce((s, d) => s + d.grossCents, 0);

/**
 * Laufende Nummer je Abzugszeile: der wievielte Abschlag DIESER Kette ist das?
 *
 * Gibt ein Feld in der Laenge der Abzugsliste zurueck, damit jede Zeile ihre
 * Nummer bekommt, ohne dass der Aufrufer selbst zaehlt.
 *
 * Gezaehlt werden BELEGE, nicht Zeilen: ein Abschlag ueber zwei Steuersaetze
 * steht mit zwei Eintraegen in der Liste und traegt in beiden dieselbe Nummer.
 * Wer Zeilen zaehlte, verspraeche in der Summenzeile "Abschlaege 1–4", wo nur
 * zwei Rechnungen liegen.
 *
 * Die Nummer ist ausdruecklich die Position IN DER ABZUGSLISTE und nicht die
 * Rechnungsnummer: die Liste ist nach Rechnungsdatum, dann Nummer sortiert
 * (deductionsFromAdvances), der erste Beleg darin ist also der erste Abschlag.
 * Eine Spanne aus Rechnungsnummern ("AR-2026-0001 bis AR-2026-0006") behauptete
 * dagegen Lueckenlosigkeit, die der Nummernkreis nicht hergibt — dazwischen
 * koennen Belege anderer Projekte liegen.
 *
 * Eintraege ohne Nummer bekommen eine eigene laufende Nummer: sie lassen sich
 * keinem anderen Beleg zuordnen. validateAdvanceChain weist sie zwar als Fehler
 * zurueck, aber solange ein Entwurf sie zeigt, darf die Zaehlung sie nicht
 * verschlucken — sonst nennt die Spanne weniger Abschlaege, als daruebersteht.
 */
export const advanceDeductionOrdinals = (deductions: AdvanceDeduction[]): number[] => {
    const byNumber = new Map<string, number>();
    let next = 0;
    return deductions.map((d) => {
        if (!d.invoiceNumber) return ++next;
        const seen = byNumber.get(d.invoiceNumber);
        if (seen !== undefined) return seen;
        byNumber.set(d.invoiceNumber, ++next);
        return next;
    });
};

/**
 * Wie viele Abschlagsrechnungen in der Abzugsliste stecken.
 *
 * Bewusst aus den laufenden Nummern abgeleitet und nicht eigenstaendig gezaehlt:
 * die Spanne "Abschlaege 1–n" der Summenzeile und die Nummern der Einzelzeilen
 * duerfen nicht auseinanderlaufen. Die letzte vergebene Nummer IST die Anzahl.
 */
export const advanceDeductionCount = (deductions: AdvanceDeduction[]): number =>
    advanceDeductionOrdinals(deductions).reduce((max, n) => (n > max ? n : max), 0);

/**
 * Die tatsaechlich gedruckte Nummerierung der Abzuege.
 *
 * EINE Stelle rechnet sie aus, und alle drei Ausgabestellen — Ueberschrift,
 * Einzelzeilen, Summenzeile — lesen von hier. Genau daran ist die alte Fassung
 * gescheitert: die Ueberschrift liess sich von Hand richtigstellen, waehrend
 * die Summenzeile darunter weiter "Abschläge 1–2 gesamt" behauptete.
 *
 * `from` verschiebt die gerechneten Nummern; die Reihenfolge und die
 * Zusammenfassung eines Belegs ueber mehrere Steuersaetze bleiben davon
 * unberuehrt (advanceDeductionOrdinals entscheidet weiter, WELCHE Zeilen
 * dieselbe Nummer tragen).
 *
 * `to` darf UEBER das hinausgehen, was der Beleg auffuehrt: die Kette der
 * Abschlaege ist laenger als der Ausschnitt, den diese Rechnung abzieht, und
 * "Abschläge 1–6" bei zwei aufgefuehrten Zeilen ist dann die richtige Aussage.
 * Unter `from` kann sie nicht fallen — eine rueckwaerts laufende Spanne waere
 * keine Angabe, sondern ein Vertipper.
 *
 * Ohne Abzuege ist nichts zu nummerieren: from und to sind dann 0, und die
 * Beschriftungen fallen ohnehin weg.
 */
export const advanceNumberingPlan = (
    deductions: AdvanceDeduction[],
    numbering?: AdvanceNumbering,
): { hidden: boolean; from: number; to: number; ordinals: number[] } => {
    const computed = advanceDeductionOrdinals(deductions);
    const count = computed.reduce((max, n) => (n > max ? n : max), 0);
    const hidden = numbering?.hidden === true;
    if (count === 0) return { hidden, from: 0, to: 0, ordinals: [] };

    const wanted = Math.trunc(numbering?.from ?? 1);
    const from = Number.isFinite(wanted) && wanted >= 1 ? wanted : 1;
    const last = from + count - 1;
    const wantedTo = Math.trunc(numbering?.to ?? last);
    const to = Number.isFinite(wantedTo) && wantedTo >= from ? wantedTo : last;

    return { hidden, from, to, ordinals: computed.map((n) => n + from - 1) };
};

/**
 * Beschriftung einer Einzelzeile des Abschlagsabzugs.
 *
 * Eine Quelle fuer Andruck und Maske. Das Datum kommt bereits formatiert herein
 * — die Datumsdarstellung haengt an der Oberflaeche, die Beschriftung nicht, und
 * dieses Modul soll sich keinen Datumsformatierer aus der PDF-Schicht einziehen.
 *
 * Ohne Rechnungsnummer bleibt die Zeile trotzdem lesbar; nur ausstellen laesst
 * sich der Beleg dann nicht (validateAdvanceChain, 'advance_without_number').
 */
export const advanceDeductionLineLabel = (
    d: AdvanceDeduction,
    ordinal: number,
    invoiceDateText: string,
    opts: { hidden?: boolean } = {},
): string => {
    const beleg = `${d.invoiceNumber || 'ohne Nummer'} vom ${invoiceDateText}`;
    // Ohne Nummerierung traegt die Belegnummer die Zuordnung allein — "Abschlag"
    // bleibt trotzdem stehen, sonst stuende in der Zeile nur eine Nummer ohne
    // Auskunft darueber, um was fuer einen Beleg es sich handelt.
    return opts.hidden ? `Abschlag — ${beleg}` : `${ordinal}. Abschlag — ${beleg}`;
};

/**
 * Beschriftung der Summenzeile unter der Einzelaufstellung.
 *
 * Nennt die SPANNE der laufenden Nummern ("Abschläge 1–6 gesamt"), so vom
 * Betreiber gefordert. Die Spanne ist wahr, weil die Einzelzeilen darueber
 * dieselben Nummern tragen (advanceDeductionLineLabel) und beide aus derselben
 * Quelle stammen.
 *
 * Gibt null zurueck, wenn weniger als zwei Abschlaege vorliegen — bei einem
 * waere die Summe die Wiederholung der Zeile darueber. Die Regel steht hier und
 * nicht in den Aufrufern, damit Andruck und Maske sie nicht getrennt
 * entscheiden und dabei auseinanderlaufen koennen.
 *
 * Beim Storno sind die Abzuege selbst negativ: dort wird nicht abgezogen,
 * sondern zurueckbelastet, und "gesamt abzueglich" waere sachlich falsch.
 *
 * Die Spanne kommt aus advanceNumberingPlan und folgt damit der Vorgabe am
 * Beleg (details.advanceNumbering) — dieselbe Quelle wie Ueberschrift und
 * Einzelzeilen. Ohne Nummerierung bleibt die Zeile bestehen und heisst
 * "Abschlagszahlungen gesamt": die Summe wird gebraucht, die Nummern nicht.
 */
export const advanceDeductionRangeLabel = (
    deductions: AdvanceDeduction[],
    opts: { reversal?: boolean; numbering?: AdvanceNumbering } = {},
): string | null => {
    const plan = advanceNumberingPlan(deductions, opts.numbering);
    if (advanceDeductionCount(deductions) < 2) return null;
    if (plan.hidden) {
        return opts.reversal ? 'Rückbelastung Abschlagszahlungen' : 'Abschlagszahlungen gesamt';
    }
    const span = plan.from === plan.to ? `Abschlag ${plan.from}` : `Abschläge ${plan.from}–${plan.to}`;
    return opts.reversal ? `Rückbelastung ${span}` : `${span} gesamt`;
};

/**
 * Spanne der Abschlaege als blosse Nennung: "Abschläge 1–4" / "Abschlag 1".
 *
 * Fuer die ZUSAMMENGEFASSTE Ansicht (GROUPED) und die Summenzeile der Maske:
 * dort steht keine Einzelaufstellung, und ohne die Spanne wuesste der Leser
 * nicht, wie viele Belege im Sammelabzug stecken. Anders als
 * advanceDeductionRangeLabel auch bei EINEM Abschlag gefuellt (Singular) —
 * hier ist sie keine Summen-Wiederholung, sondern die einzige Nennung.
 * Vorzeichenneutral: beim Storno steht sie in der Rueckbelastungs-Ueberschrift
 * genauso. Folgt der Nummerierungsvorgabe am Beleg und gibt null zurueck, wenn
 * sie ausgeschaltet ist (details.advanceNumbering.hidden).
 */
export const advanceDeductionSpanLabel = (
    deductions: AdvanceDeduction[],
    numbering?: AdvanceNumbering,
): string | null => {
    const plan = advanceNumberingPlan(deductions, numbering);
    if (plan.to === 0 || plan.hidden) return null;
    return plan.from === plan.to
        ? `Abschlag ${plan.from}`
        : `Abschläge ${plan.from}–${plan.to}`;
};

/**
 * UEBERSCHRIFT des Abzugsblocks — die fette Zeile ueber den Abzugszeilen.
 *
 * Eine Quelle fuer Andruck und Maske: die Maske zeigt denselben Text als
 * Vorgabe, den der Beleg druckt, sonst korrigiert der Bearbeiter gegen etwas
 * anderes als das, was spaeter auf dem Papier steht.
 *
 * Der Standardtext ist gerechnet:
 *  - Beim Storno und bei Korrekturen sind die Abzuege selbst negativ. Der
 *    Abschlag wird dann nicht abgezogen, sondern zurueckbelastet —
 *    "Abzueglich" waere dort sachlich falsch.
 *  - Die Spanne ("Abschläge 1–4") steht nur in der ZUSAMMENGEFASSTEN Ansicht:
 *    ohne Einzelaufstellung ist die Ueberschrift die einzige Stelle, an der
 *    sichtbar wird, wie viele Belege im Sammelabzug stecken. Bei der
 *    Einzelaufstellung traegt jede Zeile ihre Nummer.
 *
 * `numbering` (details.advanceNumbering) verschiebt die Spanne, und
 * `override` (details.advanceDeductionHeading) verdraengt den Standardtext
 * VOLLSTAENDIG — samt Spanne, denn genau die ist der haeufige Korrekturgrund.
 * Ein eingegebener Doppelpunkt am Ende wird abgeraeumt: den setzt die
 * Ausgabe, und "…zahlungen::" waere das Ergebnis.
 *
 * Ohne Doppelpunkt zurueckgegeben — die Ausgabe haengt ihn an.
 */
export const advanceDeductionHeadingLabel = (
    deductions: AdvanceDeduction[],
    opts: { mode?: AdvanceDeductionMode; override?: string; numbering?: AdvanceNumbering } = {},
): string => {
    const override = opts.override?.trim().replace(/:+$/, '').trim();
    if (override) return override;

    const reversal = deductions.length > 0 && deductions.every((d) => d.grossCents <= 0);
    const span =
        opts.mode === 'GROUPED' ? advanceDeductionSpanLabel(deductions, opts.numbering) : null;
    const suffix = span ? ` (${span})` : '';
    return reversal
        ? `Rückbelastung erhaltener Abschlagszahlungen${suffix}`
        : `Abzüglich erhaltener Abschlagszahlungen${suffix}`;
};

/**
 * Steuerbetrag einer Abschlagsposition aus dem Nettobetrag.
 *
 * Eigene kleine Huelle um taxFromNet, damit an genau einer Stelle steht, dass
 * ein Abschlag seine Steuer wie jede andere Leistung ausweist — er ist eine
 * Rechnung, keine blosse Zahlungsaufforderung.
 */
export const advanceTaxCents = (netCents: Cents, taxRateBp: number): Cents =>
    taxFromNet(netCents, taxRateBp);

// ═══════════════════════════════════════════════════════════════════════════
// Zahlbetrag: netto und brutto
// ═══════════════════════════════════════════════════════════════════════════

/** Ein Steuersatz des Restbetrags. */
export interface PayableBreakdownGroup {
    taxRateBp: number;
    taxCategoryCode: TaxCategoryCode;
    /** Nettoanteil des Restbetrags in diesem Satz. */
    netCents: Cents;
    /** Umsatzsteueranteil des Restbetrags in diesem Satz. */
    taxCents: Cents;
    grossCents: Cents;
}

/** Restbetrag, aufgeteilt in netto und Umsatzsteuer. */
export interface PayableBreakdown {
    netCents: Cents;
    taxCents: Cents;
    /**
     * Der RECHNUNGSBETRAG brutto (gross - prepaid), nicht der Zahlbetrag:
     * ohne Sicherheitseinbehalt sind beide gleich, mit Einbehalt bzw.
     * Auszahlung bleibt die Aufteilung auf dem Betrag VOR Einbehalt — der
     * Einbehalt hat keinen Steueranteil, eine Aufteilung des Zahlbetrags
     * waere erfunden.
     */
    grossCents: Cents;
    groups: PayableBreakdownGroup[];
    /**
     * Ob netto + Steuer den Rechnungsbetrag ergeben. Falsch nur bei Belegen,
     * deren Abzugszeilen in sich nicht aufgehen (grossCents ist nicht netCents
     * + taxCents) — dann darf die Aufschluesselung nicht gedruckt werden, weil
     * sie neben dem Betrag zwei Zahlen zeigte, die ihn nicht ergeben.
     */
    consistent: boolean;
}

/**
 * Was vom Beleg noch netto und was als Umsatzsteuer offen ist.
 *
 * Der Zahlbetrag ist ein BRUTTObetrag: er entsteht aus dem Bruttobetrag der
 * Gesamtleistung abzueglich der BRUTTOsummen der Abschlaege. Nur brutto
 * ausgewiesen, ist am Beleg nicht mehr zu erkennen, welcher Teil der Forderung
 * Entgelt ist und welcher Umsatzsteuer — beim Kunden ist genau das die Frage,
 * die vor der Zahlung gestellt wird.
 *
 * Rein NACHRICHTLICH. Der Steuerausweis nach §14 UStG bleibt der Steuerblock
 * ueber der Gesamtleistung; diese Aufteilung mindert weder das ausgewiesene
 * Netto noch die ausgewiesene Steuer und darf nie an deren Stelle treten
 * (dieselbe Regel wie im Kopf dieser Datei).
 *
 * Gerechnet wird je Steuersatz, nicht ueber alles: bei zwei Saetzen waere eine
 * einzige Nettozahl nicht mehr zuzuordnen. Ein Satz, den nur die Abzuege
 * kennen, erscheint mit negativen Anteilen — das ist ein Datenfehler und wird
 * gezeigt statt verschluckt.
 */
export const payableBreakdown = (
    totals: InvoiceTotals,
    deductions: AdvanceDeduction[],
): PayableBreakdown => {
    const byRate = new Map<number, PayableBreakdownGroup>();

    for (const g of totals.taxGroups) {
        byRate.set(g.taxRateBp, {
            taxRateBp: g.taxRateBp,
            taxCategoryCode: g.taxCategoryCode,
            netCents: g.taxableNetCents,
            taxCents: g.taxCents,
            grossCents: g.taxableNetCents + g.taxCents,
        });
    }

    for (const d of deductions) {
        const g = byRate.get(d.taxRateBp);
        if (g) {
            g.netCents -= d.netCents;
            g.taxCents -= d.taxCents;
            g.grossCents = g.netCents + g.taxCents;
        } else {
            byRate.set(d.taxRateBp, {
                taxRateBp: d.taxRateBp,
                taxCategoryCode: 'S',
                netCents: -d.netCents,
                taxCents: -d.taxCents,
                grossCents: -(d.netCents + d.taxCents),
            });
        }
    }

    const groups = [...byRate.values()].sort((a, b) => b.taxRateBp - a.taxRateBp);
    const netCents = groups.reduce((s, g) => s + g.netCents, 0);
    const taxCents = groups.reduce((s, g) => s + g.taxCents, 0);

    // Bezugsgroesse ist der Rechnungsbetrag vor Einbehalt und Freigabe
    // (gross - prepaid): der Einbehalt haelt einen Teil des Zahlbetrags
    // zurueck, aendert aber weder Entgelt noch Steuer. Gegen den Zahlbetrag
    // geprueft, waere die Aufteilung auf jedem Beleg mit Einbehalt
    // "inkonsistent" und fiele vom Papier.
    const basisCents = invoiceAmountCents(totals.grossTotalCents, totals.prepaidCents);

    return {
        netCents,
        taxCents,
        grossCents: basisCents,
        groups,
        consistent: netCents + taxCents === basisCents,
    };
};
